import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './MiniDashboardDisplay.module.scss';

const MiniChart = ({ type, data }) => {
  switch (type) {
    case 'donut': {
      const total =
        Number(data.total) ||
        (Array.isArray(data.segments)
          ? data.segments.reduce((s, seg) => s + Number(seg.value || 0), 0)
          : 0);
      let startDeg = 0;
      const gradientParts = Array.isArray(data.segments)
        ? data.segments.map(segment => {
            const percent = total > 0 ? Number(segment.value || 0) / total : 0;
            const endDeg = startDeg + percent * 360;
            const part = `${segment.color || '#2185d0'} ${startDeg}deg ${endDeg}deg`;
            startDeg = endDeg;
            return part;
          })
        : [];
      const gradient = `conic-gradient(${gradientParts.join(', ')})`;
      const v1 = data.segments?.[0]?.value ?? 0;
      const v2 = data.segments?.[1]?.value ?? 0;

      return (
        <div>
          <div className={styles.miniDonut}>
            <div className={styles.miniDonutInner} style={{ background: gradient }}>
              <span className={styles.miniDonutValue}>{data.total}</span>
            </div>
          </div>
          <div className={styles.miniDonutCaption}>{`${v1}/${v2}`}</div>
        </div>
      );
    }
    case 'bar':
      return (
        <div className={styles.miniBars}>
          {data.bars &&
            data.bars.slice(0, 4).map((bar, index) => (
              <div
                key={index}
                className={styles.miniBar}
                style={{
                  height: `${(bar.value / Math.max(...data.bars.map(b => b.value))) * 100}%`,
                  backgroundColor: bar.color,
                }}
              />
            ))}
        </div>
      );
    case 'line':
      return (
        <div className={styles.miniLine}>
          <svg viewBox="0 0 60 30" className={styles.miniLineSvg}>
            {data.points && data.points.length > 1 && (
              <polyline
                fill="none"
                stroke={data.color || '#2185d0'}
                strokeWidth="2"
                points={data.points
                  .map(
                    (point, index) =>
                      `${(index / (data.points.length - 1)) * 55 + 2.5},${
                        27 - (point / Math.max(...data.points)) * 22
                      }`
                  )
                  .join(' ')}
              />
            )}
          </svg>
        </div>
      );
    default:
      return <div className={styles.miniChartDefault}>📊</div>;
  }
};

const MiniDashboardDisplay = ({ dashboardData, className, onClick }) => {
  const [isApproved, setIsApproved] = React.useState(false);

  if (!dashboardData) return null;

  const { type, title, data } = dashboardData;

  const renderPreview = () => {
    switch (type) {
      case 'metrics':
        const primaryMetric = data.metrics && data.metrics[0];
        const primaryChart = data.charts && data.charts[0];

        return (
          <div className={styles.metricsPreview}>
            {primaryMetric && (
              <div
                className={classNames(styles.miniMetric, styles[`status-${primaryMetric.status}`])}
              >
                <div className={styles.miniMetricValue}>{primaryMetric.value}</div>
                <div className={styles.miniMetricTitle}>{primaryMetric.title}</div>
              </div>
            )}
            {primaryChart && (
              <div className={styles.miniChartContainer}>
                <MiniChart type={primaryChart.type} data={primaryChart.data} />
              </div>
            )}
          </div>
        );

      case 'image_report':
        return (
          <div className={styles.imagePreview}>
            <div className={styles.miniImageContainer}>
              <img src={data.src} alt={data.title} className={styles.miniImage} />
              <div className={styles.miniImageOverlay}>
                <button
                  className={classNames(styles.miniApprovalButton, {
                    [styles.approved]: isApproved,
                    [styles.pending]: !isApproved,
                  })}
                  onClick={e => {
                    e.stopPropagation(); // Prevent card click
                    setIsApproved(!isApproved);
                  }}
                >
                  {isApproved ? '✓' : 'A'}
                </button>
              </div>
            </div>
            <div className={styles.miniImageContent}>
              <div className={styles.miniImageTitle}>{data.title}</div>
            </div>
          </div>
        );

      case 'performance':
        const primaryKpi = data.kpis && data.kpis[0];
        const performanceChart = data.charts && data.charts[0];

        return (
          <div className={styles.performancePreview}>
            {primaryKpi && (
              <div className={classNames(styles.miniKpi, styles[`status-${primaryKpi.status}`])}>
                <div className={styles.miniKpiValue}>{primaryKpi.value}</div>
                <div className={styles.miniKpiTitle}>{primaryKpi.title}</div>
              </div>
            )}
            {performanceChart && (
              <div className={styles.miniPerformanceChart}>
                <MiniChart type={performanceChart.type} data={performanceChart.data} />
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className={styles.defaultPreview}>
            <div className={styles.defaultIcon}>📊</div>
            <div className={styles.defaultTitle}>{title || 'Dashboard'}</div>
          </div>
        );
    }
  };

  return (
    <div
      className={classNames(styles.wrapper, className, {
        [styles.clickable]: onClick,
      })}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className={styles.preview}>{renderPreview()}</div>
      {onClick && (
        <div className={styles.clickIndicator}>
          <span>Click to view dashboard</span>
        </div>
      )}
    </div>
  );
};

MiniDashboardDisplay.propTypes = {
  dashboardData: PropTypes.shape({
    type: PropTypes.string.isRequired,
    title: PropTypes.string,
    data: PropTypes.object.isRequired,
  }),
  className: PropTypes.string,
  onClick: PropTypes.func,
};

MiniDashboardDisplay.defaultProps = {
  dashboardData: null,
  className: '',
  onClick: null,
};

MiniChart.propTypes = {
  type: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
};

export default MiniDashboardDisplay;
